/**
 * ReviewStep — create-wizard step 10 (W12 Part B, Task B6).
 *
 * Props-in / JSX-out. The order IS the contract (approved canvas v3.3):
 *
 *   1. readiness rows        — the verdict, blocker first with its fix
 *   2. cloned-variables note — neutral, no Edit: it describes a CONSEQUENCE of
 *                              creating, not an input the operator can change here
 *   3. «What you get»        — the screens switch (ON by default) plus the two
 *                              ESTIMATE rows, both labelled *estimated*
 *   4. «Also sent»           — everything the request carries that has no line
 *                              of its own, each with an Edit back to its step
 *
 * Honesty rules: no row promises a DURATION (designer invariant 22); an
 * estimate that cannot be computed reads «—» with the reason, never a number.
 */
import React from 'react';
import { Alert, Box, Button, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { STRATEGY_LIST } from '@test-kit/selectors/strategy-list';
import {
  StrategySubmitErrorAlert,
  type StrategySubmitError,
} from '../../StrategySubmitErrorAlert';
import {
  ESTIMATE_UNAVAILABLE_DETAIL,
  GENERATION_ESTIMATE_REFERENCE,
  estimateGeneration,
  formatEstimate,
} from '../generationEstimate';

/** One «Also sent» line: what it is, what it says, and the step that owns it. */
export interface ReviewAlsoSentRow {
  /** Stable key — the selector suffix AND the argument `onEdit` receives. */
  key: string;
  label: string;
  value: string;
}

/**
 * What the strategy will CLONE on creation (read-only consequence, no Edit).
 *
 * TWO explicit numbers, each from its own correct source (designer invariant 2;
 * Task B9d must-fix 4 + fix round 1). Create-mode cloning copies the WHOLE
 * project variable set (`structuredClone(project.variableSet.variables)`),
 * while the readiness row above reports the package this generation SENDS —
 * identical on the default path, different the moment the operator unticks a
 * variable. Stating one number for both is what made the screen unanswerable;
 * so is stating a single unqualified total. No `a + b` notation.
 */
export interface ReviewVariablesSummary {
  /** M — the WHOLE project variable set, which is what creating clones. */
  clonedCount: number;
  /** N — project variables in the package this generation sends (M ≥ N). */
  sentCount: number;
  /** CLONED variables with no analytics decision yet, by human name. */
  needReviewNames: string[];
  /** An insight-variable generation is chosen (else the project has nothing). */
  hasGeneration: boolean;
}

export interface ReviewStepProps {
  /**
   * The `GenerationReadinessStrip layout="rows"` element. A slot rather than
   * nine forwarded props: the strip's inputs are all dialog-owned and this step
   * has no opinion about any of them.
   */
  readiness: React.ReactNode;
  variables: ReviewVariablesSummary;
  generateScreens: boolean;
  onGenerateScreensChange: (value: boolean) => void;
  /** Task routes in the request — the estimate's only size input. */
  routes: number;
  modelName: string;
  modelInputUsdPerMTokens?: number;
  modelOutputUsdPerMTokens?: number;
  alsoSent: ReviewAlsoSentRow[];
  /** Jumps to the step that owns `key` (an «Also sent» key). */
  onEdit: (key: string) => void;
  /** Rendered last — «Export Strategy JSON» in the dialog. */
  footer?: React.ReactNode;
  /**
   * F-LIVE2-40: a library entry is selected as `adapt` while the screens switch
   * below is OFF — the exact contradiction the server refuses with a 400. The
   * step PREVIEWS that refusal honestly; ruling FW9-R1 keeps the primary
   * enabled, because a client warning never duplicates a server gate as a
   * second disabled state.
   */
  adaptNeedsScreens?: boolean;
  /**
   * F-LIVE2-40: the submit's own failure, rendered where the operator is
   * standing instead of in a browser `alert()`. `refused` means the server
   * validated the request away and NOTHING was created.
   */
  submitError?: StrategySubmitError | null;
}

const SECTION_LABEL_SX = { fontWeight: 500, pb: 0.5 } as const;
const ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  py: 1,
  borderTop: 1,
  borderColor: 'divider',
} as const;
const ROW_LABEL_SX = { width: 206, flexShrink: 0 } as const;

/** An «Also sent» row: label · value · Edit. */
const AlsoSentRow: React.FC<{ row: ReviewAlsoSentRow; onEdit: (key: string) => void }> = ({
  row,
  onEdit,
}) => (
  <Box sx={ROW_SX} data-testid={STRATEGY_LIST.wizardReviewAlsoSent(row.key)}>
    <Typography variant="body2" color="text.secondary" sx={ROW_LABEL_SX}>{row.label}</Typography>
    <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>{row.value}</Typography>
    <Button
      variant="text"
      size="small"
      onClick={() => onEdit(row.key)}
      aria-label={`Edit ${row.label}`}
      data-testid={STRATEGY_LIST.wizardReviewEdit(row.key)}
      sx={{ flexShrink: 0, textTransform: 'none', color: 'primary.dark' }}
    >
      Edit
    </Button>
  </Box>
);

/** An estimate row: label · value · detail. Never an Edit — nothing to edit. */
const EstimateRow: React.FC<{ testId: string; label: string; value: string; detail: string }> = ({
  testId,
  label,
  value,
  detail,
}) => (
  <Box sx={ROW_SX} data-testid={testId}>
    <Typography variant="body2" color="text.secondary" sx={ROW_LABEL_SX}>{label}</Typography>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2">{value}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {detail}
      </Typography>
    </Box>
  </Box>
);

export const ReviewStep: React.FC<ReviewStepProps> = ({
  readiness,
  variables,
  generateScreens,
  onGenerateScreensChange,
  routes,
  modelName,
  modelInputUsdPerMTokens,
  modelOutputUsdPerMTokens,
  alsoSent,
  onEdit,
  footer,
  adaptNeedsScreens = false,
  submitError = null,
}) => {
  const estimate = estimateGeneration({ routes, generateScreens, modelOutputUsdPerMTokens });
  const formatted = formatEstimate(estimate, routes);
  const estimable = estimate.screens !== null;

  const screensValue = estimable && !generateScreens
    ? '0 screens · block plan only'
    : formatted.screens;
  const screensDetail = !estimable
    ? ESTIMATE_UNAVAILABLE_DETAIL
    : generateScreens
      ? `estimated · about ${GENERATION_ESTIMATE_REFERENCE.screensPerRoute} per route`
        + ' (measured on a reference generation) · the AI decides the exact number per block'
      : `estimated · turning the switch on adds about ${GENERATION_ESTIMATE_REFERENCE.screensPerRoute}`
        + ' screens per route';

  const priceClause = modelInputUsdPerMTokens !== undefined && modelOutputUsdPerMTokens !== undefined
    ? ` at $${modelInputUsdPerMTokens.toFixed(2)} / $${modelOutputUsdPerMTokens.toFixed(2)} per 1M tokens`
    : '';
  const costDetail = !estimable
    ? ESTIMATE_UNAVAILABLE_DETAIL
    // A model the project's catalog carries no price for: say THAT, never a
    // number borrowed from the reference model.
    : estimate.usd === null
      ? `no published price for ${modelName} — the cost cannot be estimated`
      : `estimated · ${modelName}${priceClause}`
        + ` · ${generateScreens ? 'plan + screens' : 'block plan only'} · billed by actual usage`;

  const needReview = variables.needReviewNames.length;
  const shownNames = variables.needReviewNames.slice(0, 5).join(', ');
  const restNames = needReview - 5;

  return (
    <Stack spacing={2.5}>
      {/* F-LIVE2-40: the refusal speaks HERE — on the step the operator is
          standing on, next to the primary that produced it — never through a
          browser `alert()` (the channel F-LIVE2-23a / F-LIVE2-28 flagged). The
          copy lives in the shared component, which the quick-create screen
          mounts too: one rule, two surfaces (fix round 1, review #2/#4). */}
      <StrategySubmitErrorAlert error={submitError} />

      {/* F-LIVE2-40: the honest preview of the server's `adapt` + screens-OFF
          refusal. A WARNING, not a gate (ruling FW9-R1) — the primary stays
          enabled and the server stays the one that refuses. */}
      {adaptNeedsScreens && (
        <Alert
          severity="warning"
          data-testid={STRATEGY_LIST.wizardAdaptScreensWarning}
        >
          Adapt needs screens: the adapted example is applied while screens are generated. Turn
          screens on or switch the entry to Include.
        </Alert>
      )}

      {readiness}

      {/* UXR C6 (AC-022): a readable guidance summary of what creating CLONES —
          never a disabled table. Editing happens after creation (strategy edit)
          or in project settings, so this row carries no Edit of its own. */}
      <Box data-testid={STRATEGY_LIST.variablesGuidanceSummary}>
        <Typography variant="body2" color="text.secondary">
          {`After creation the strategy gets its own copy of the ${variables.clonedCount} project `
            + 'variables (editable after creation);'
            + ` ${variables.sentCount} of them are used by this generation.`}
          {needReview > 0
            ? ` ${needReview} need review — an analytics decision you can resolve after creation`
              + ' (strategy edit) or in project settings.'
            : ''}
        </Typography>
        {needReview > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {`Need review: ${shownNames}${restNames > 0 ? ` +${restNames} more` : ''}`}
          </Typography>
        )}
        {variables.clonedCount === 0 && !variables.hasGeneration && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: 'italic', mt: 0.5 }}
          >
            No variables defined on the project yet. You can add them later in strategy edit mode.
          </Typography>
        )}
      </Box>

      <Box>
        <Typography variant="body2" sx={SECTION_LABEL_SX}>What you get</Typography>

        {/* Screens are generated with the plan by DEFAULT; the switch that turns
            them off lives here, next to what it changes (owner ruling). */}
        <Box sx={{ ...ROW_SX, display: 'block' }}>
          <FormControlLabel
            data-testid={STRATEGY_LIST.formGenerateScreens}
            control={(
              <Switch
                checked={generateScreens}
                onChange={(event) => onGenerateScreensChange(event.target.checked)}
                size="small"
              />
            )}
            label="Also generate screens for each block"
            slotProps={{ typography: { variant: 'body2' } }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 6 }}>
            On: every block gets its screens in the same run. Off: the block plan only — screens
            later, per block, from the canvas.
          </Typography>
        </Box>

        <EstimateRow
          testId={STRATEGY_LIST.wizardReviewScreensRow}
          label="Screens"
          value={screensValue}
          detail={screensDetail}
        />
        <EstimateRow
          testId={STRATEGY_LIST.wizardReviewCostRow}
          label="Cost"
          value={formatted.cost}
          detail={costDetail}
        />
      </Box>

      <Box>
        <Typography variant="body2" sx={SECTION_LABEL_SX}>Also sent</Typography>
        {alsoSent.map((row) => (
          <AlsoSentRow key={row.key} row={row} onEdit={onEdit} />
        ))}
      </Box>

      {footer}
    </Stack>
  );
};

ReviewStep.displayName = 'ReviewStep';
